// Land Use Service - Simplified to use GovMap retzefmigrashim layer
// Land use is now automatically fetched via govmap-layers API (retzefmigrashim layer 11)
// and is available in propertyInfo.advancedLayers.landUse

class LandUseService {
  /**
   * Get detailed land use designation
   * Note: Land use is now automatically included in GovMapService via retzefmigrashim layer
   * This method is kept for backward compatibility but returns null
   * @param {Object} coordinates - {x, y} in Israeli Grid (ITM)
   * @returns {Promise<Object>} Land use data (now returns null - use advancedLayers.landUse)
   */
  static async getDetailedLandUse(coordinates) {
    if (!coordinates || !coordinates.x || !coordinates.y) {
      console.warn('⚠️ No coordinates provided for land use lookup');
      return null;
    }

    console.log('ℹ️ Land use is now automatically fetched via GovMap retzefmigrashim layer (layer 11)');
    console.log('ℹ️ Access via propertyInfo.advancedLayers.landUse');
    
    // Land use is now automatically fetched via govmap-layers API
    // No additional API call needed here
    return null;
  }

  /**
   * Get land use description in Hebrew with context
   * @param {string} mavatName - The MAVAT land use designation
   * @param {number} mavatCode - The MAVAT code
   * @returns {string} Formatted Hebrew description
   */
  static formatLandUseDescription(mavatName, mavatCode) {
    if (!mavatName) return 'לא ידוע';

    // Common land use types and their descriptions
    const descriptions = {
      10: 'מגורים', // General residential
      20: 'מגורים א\'', // Residential zone A
      21: 'מגורים ב\'', // Residential zone B
      22: 'מגורים ג\'', // Residential zone C
      1000: 'מגורים ומסחר', // Mixed residential/commercial
      1050: 'מגורים מסחר ותעסוקה', // Mixed use
      995: 'יעוד עפ"י תכנית מאושרת אחרת', // Per other approved plan
      820: 'דרך מאושרת', // Approved road
      // Add more as needed
    };

    // Return known description or the mavat name
    return descriptions[mavatCode] || mavatName;
  }

  /**
   * Get land use suitable for display in UI
   * Now primarily uses data from propertyInfo.advancedLayers.landUse (retzefmigrashim)
   * Falls back to buildingData or plans if needed
   */
  static async getLandUseForDisplay(propertyInfo, plans = []) {
    // 1. Try retzefmigrashim from advanced layers (most accurate)
    if (propertyInfo?.advancedLayers?.landUse?.designation) {
      return {
        value: propertyInfo.advancedLayers.landUse.designation,
        source: 'retzefmigrashim',
        sourceLabel: 'GovMap - רצף מגרשי תבע',
        plan: propertyInfo.advancedLayers.landUse.plan,
        confidence: 'high'
      };
    }

    // 2. Try from building data (e.g., Tel Aviv GIS)
    if (propertyInfo?.buildingData?.landUse) {
      return {
        value: propertyInfo.buildingData.landUse,
        source: 'municipal_gis',
        sourceLabel: 'מערכת GIS עירונית',
        confidence: 'high'
      };
    }

    // 3. Try from plans (pl_landuse_string from Layer 1)
    if (plans && plans.length > 0) {
      const approvedPlans = plans.filter(p => 
        (p.status === 'מאושר' || p.statusType === 'approved' || p.status?.includes('אושר')) &&
        p.landUse && 
        p.landUse !== 'לא ידוע' &&
        p.landUse !== 'יעוד עפ"י תכנית מאושרת אחרת'
      );

      if (approvedPlans.length > 0) {
        // Sort by most recent
        approvedPlans.sort((a, b) => {
          const dateA = a.approvalDate || a.publicationDate || 0;
          const dateB = b.approvalDate || b.publicationDate || 0;
          return dateB - dateA;
        });

        return {
          value: approvedPlans[0].landUse,
          source: 'xplan_layer1',
          sourceLabel: 'xplan - תכניות',
          plan: {
            number: approvedPlans[0].planNumber,
            name: approvedPlans[0].planName
          },
          confidence: 'medium'
        };
      }
    }

    // 4. No data found
    return {
      value: 'לא ידוע',
      source: 'none',
      sourceLabel: 'לא זמין',
      confidence: 'none'
    };
  }
}

export default LandUseService;
